/**
 * AI Rate Limit Settings Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Rate limiting configuration for AI requests.
 */

import React from 'react';
import { Clock, Users, Building } from 'lucide-react';
import type { AISettingsUpdateData } from '../../../types/admin';

interface AIRateLimitSettingsProps {
  formData: AISettingsUpdateData;
  onChange: (updates: Partial<AISettingsUpdateData>) => void;
}

export const AIRateLimitSettings: React.FC<AIRateLimitSettingsProps> = ({ formData, onChange }) => {
  const userLimit = formData.max_requests_per_user_per_hour ?? 100;
  const accountLimit = formData.max_requests_per_account_per_day ?? 10000;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Rate Limits</h3>
        <p className="text-sm text-gray-500 mt-1">
          Control the maximum number of AI requests to manage costs and prevent abuse.
        </p>
      </div>

      {/* User Limit */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users size={20} className="text-blue-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Per-User Limit</h4>
            <p className="text-sm text-gray-500">Maximum requests per user per hour</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <input
            type="range"
            min={10}
            max={500}
            step={10}
            value={userLimit}
            onChange={(e) =>
              onChange({ max_requests_per_user_per_hour: parseInt(e.target.value, 10) })
            }
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#002B45]"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={1000}
              value={userLimit}
              onChange={(e) =>
                onChange({ max_requests_per_user_per_hour: parseInt(e.target.value, 10) || 100 })
              }
              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
            <span className="text-sm text-gray-500">/hour</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>10</span>
          <span>100</span>
          <span>250</span>
          <span>500</span>
        </div>
      </div>

      {/* Account Limit */}
      <div className="p-4 bg-white border border-gray-200 rounded-lg space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-lg">
            <Building size={20} className="text-purple-600" />
          </div>
          <div>
            <h4 className="font-medium text-gray-900">Account-Wide Limit</h4>
            <p className="text-sm text-gray-500">Maximum requests for entire account per day</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <input
            type="range"
            min={1000}
            max={50000}
            step={1000}
            value={accountLimit}
            onChange={(e) =>
              onChange({ max_requests_per_account_per_day: parseInt(e.target.value, 10) })
            }
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#002B45]"
          />
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={100}
              max={100000}
              value={accountLimit}
              onChange={(e) =>
                onChange({ max_requests_per_account_per_day: parseInt(e.target.value, 10) || 10000 })
              }
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
            <span className="text-sm text-gray-500">/day</span>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>1K</span>
          <span>10K</span>
          <span>25K</span>
          <span>50K</span>
        </div>
      </div>

      {/* Info Box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium">Rate Limit Behavior</p>
            <ul className="mt-1 list-disc list-inside text-xs">
              <li>Users who exceed their limit will see a friendly message to wait</li>
              <li>Limits reset on a rolling window (user: hourly, account: daily)</li>
              <li>Rate-limited requests are logged for monitoring</li>
              <li>
                Higher limits may increase AI costs - adjust based on usage patterns
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Quick Presets</h4>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Conservative', user: 50, account: 5000 },
            { label: 'Standard', user: 100, account: 10000 },
            { label: 'High Usage', user: 200, account: 25000 },
            { label: 'Enterprise', user: 500, account: 50000 },
          ].map((preset) => (
            <button
              key={preset.label}
              onClick={() =>
                onChange({
                  max_requests_per_user_per_hour: preset.user,
                  max_requests_per_account_per_day: preset.account,
                })
              }
              className={`
                px-3 py-1.5 text-sm border rounded-lg transition-colors
                ${userLimit === preset.user && accountLimit === preset.account
                  ? 'bg-[#002B45] text-white border-[#002B45]'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                }
              `}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIRateLimitSettings;
